import { useCallback, useId, useState } from 'react';
import { Pencil, UserRound } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksCard } from '../../../components/liks/LiksCard/LiksCard';
import { LiksConfirmDialog } from '../../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import { LiksSwitch } from '../../../components/liks/LiksSwitch/LiksSwitch';
import { LiksPhotoUpload } from '../../../components/liks/LiksPhotoUpload/LiksPhotoUpload';
import { LiksToggleGroup } from '../../../components/liks/LiksToggleGroup/LiksToggleGroup';
import { useAuth } from '../../../auth/useAuth';
import { useI18n } from '../../../i18n/useI18n';
import { useTheme } from '../../../theme/useTheme';
import { SUPPORTED_LOCALES } from '../../../i18n/resources';
import { ChangePasswordDialog } from '../../../features/user/components/ChangePasswordDialog';
import { useUpdateCurrentUserPasswordMutation } from '../../../features/user/mutations/useUpdateCurrentUserPasswordMutation';
import { useProfilePhotoFormState } from '../../../features/user/useProfilePhotoFormState';
import { downloadTenantUserPhoto } from '../../../features/tenant/downloadTenantUserPhoto';
import {
  useDeleteTenantUserPhotoMutation,
  useUploadTenantUserPhotoMutation,
} from '../../../features/tenant/mutations/useTenantUserMutations';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import { PROFILE_PHOTO_MAX_BYTES } from '../../../utils/imageUtils';

export function SettingsProfileTab() {
  const { user } = useAuth();
  const { locale, setLocale, translate } = useI18n();
  const { theme, setTheme } = useTheme();
  const {
    replacePlatformChatDraftTextOnTranscription,
    setReplacePlatformChatDraftTextOnTranscription,
    keepSidebarOpen,
    setKeepSidebarOpen,
  } = useUserConfig();
  const photoFieldId = useId();
  const speechReplaceSwitchId = useId();
  const keepSidebarOpenSwitchId = useId();
  const [photoReloadKey, setPhotoReloadKey] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const uploadPhotoMutation = useUploadTenantUserPhotoMutation();
  const deletePhotoMutation = useDeleteTenantUserPhotoMutation();
  const updatePasswordMutation = useUpdateCurrentUserPasswordMutation();
  const isBusy = uploadPhotoMutation.isPending || deletePhotoMutation.isPending;

  const loadPhoto = useCallback(
    () => downloadTenantUserPhoto(user?.id ?? ''),
    [user?.id],
  );

  const { previewSrc: photoDisplaySrc, handlePhotoChange } =
    useProfilePhotoFormState({
      loadPhoto,
      translate,
      invalidTypeKey: 'settings.profileIcon.invalidType',
      tooLargeKey: 'settings.profileIcon.tooLarge',
      reloadKey: photoReloadKey,
    });

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!user?.id) {
      return;
    }

    const selectedFile = event.target.files?.[0] ?? null;

    void (async () => {
      const file = await handlePhotoChange(event);
      if (!file) {
        if (selectedFile) {
          const description = !selectedFile.type.startsWith('image/')
            ? translate('settings.profileIcon.invalidType')
            : selectedFile.size > PROFILE_PHOTO_MAX_BYTES
              ? translate('settings.profileIcon.tooLarge')
              : undefined;
          toast.error({
            title: translate('settings.profileIcon.uploadErrorTitle'),
            description,
          });
        }
        return;
      }

      uploadPhotoMutation.mutate(
        { userId: user.id, file },
        {
          onSuccess: () => {
            toast.success({
              title: translate('settings.profileIcon.uploadSuccessTitle'),
            });
            setPhotoReloadKey((key) => key + 1);
          },
          onError: (error) => {
            void toastMutationError(
              error,
              translate('settings.profileIcon.uploadErrorTitle'),
            );
          },
        },
      );
    })();
  };

  const handleDelete = () => {
    if (!user?.id || deletePhotoMutation.isPending) {
      return;
    }
    deletePhotoMutation.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          toast.success({
            title: translate('settings.profileIcon.removeSuccessTitle'),
          });
          setIsDeleteDialogOpen(false);
          setPhotoReloadKey((key) => key + 1);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('settings.profileIcon.removeErrorTitle'),
          );
        },
      },
    );
  };

  const handleChangePassword = (
    currentPassword: string,
    newPassword: string,
  ) => {
    updatePasswordMutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast.success({
            title: translate('settings.password.successTitle'),
          });
          setIsChangePasswordOpen(false);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('settings.password.errorTitle'),
            translate('settings.password.errorDescription'),
          );
        },
      },
    );
  };

  const themeOptions = [
    { value: 'light', label: translate('settings.theme.light') },
    { value: 'dark', label: translate('settings.theme.dark') },
  ];

  const languageOptions = SUPPORTED_LOCALES.map((language) => ({
    value: language,
    label: translate(`common.language.${language}`),
  }));

  return (
    <div className="settings-page__profile-tab">
      <LiksCard
        className="settings-page__stacked-card"
        title={translate('settings.profileIcon.title')}
        description={translate('settings.profileIcon.description')}
      >
        <LiksPhotoUpload
          src={photoDisplaySrc}
          isLoading={isBusy}
          placeholder={<UserRound strokeWidth={1.5} aria-hidden />}
          inputId={photoFieldId}
          disabled={isBusy}
          onFileChange={handleFileInputChange}
          onRemove={() => setIsDeleteDialogOpen(true)}
        />
      </LiksCard>

      <LiksCard
        className="settings-page__stacked-card"
        title={translate('settings.password.title')}
      >
        <LiksButton
          type="button"
          variant="primary"
          onClick={() => setIsChangePasswordOpen(true)}
        >
          <Pencil aria-hidden /> {translate('settings.password.change')}
        </LiksButton>
      </LiksCard>

      <LiksCard
        title={translate('settings.theme.title')}
        description={translate('settings.theme.description')}
      >
        <LiksToggleGroup
          name="theme"
          legend={translate('settings.theme.legend')}
          value={theme}
          options={themeOptions}
          onChange={(value) => setTheme(value as 'light' | 'dark')}
        />
      </LiksCard>

      <LiksCard
        title={translate('settings.language.title')}
        description={translate('settings.language.description')}
      >
        <LiksToggleGroup
          name="language"
          legend={translate('settings.language.legend')}
          value={locale}
          options={languageOptions}
          onChange={(value) => setLocale(value as typeof locale)}
        />
      </LiksCard>

      <LiksCard
        title={translate('settings.keepSidebarOpen.title')}
        description={translate('settings.keepSidebarOpen.description')}
      >
        <LiksSwitch
          id={keepSidebarOpenSwitchId}
          checked={keepSidebarOpen}
          aria-label={translate('settings.keepSidebarOpen.switchLabel')}
          onChange={(checked) => {
            setKeepSidebarOpen(checked);
            toast.info({
              title: checked
                ? translate('settings.keepSidebarOpen.turnedOnToast')
                : translate('settings.keepSidebarOpen.turnedOffToast'),
              durationMs: 2000,
            });
          }}
        />
      </LiksCard>

      <LiksCard
        title={translate('settings.speechInputReplace.title')}
        description={translate('settings.speechInputReplace.description')}
      >
        <LiksSwitch
          id={speechReplaceSwitchId}
          checked={replacePlatformChatDraftTextOnTranscription}
          aria-label={translate('settings.speechInputReplace.switchLabel')}
          onChange={(checked) => {
            setReplacePlatformChatDraftTextOnTranscription(checked);
            toast.info({
              title: checked
                ? translate('settings.speechInputReplace.turnedOnToast')
                : translate('settings.speechInputReplace.turnedOffToast'),
              durationMs: 2000,
            });
          }}
        />
      </LiksCard>

      <LiksConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={translate('settings.profileIcon.removeConfirmTitle')}
        message={translate('settings.profileIcon.removeConfirmMessage')}
        confirmLabel={translate('settings.profileIcon.removeConfirmAction')}
        cancelLabel={translate('settings.profileIcon.removeConfirmCancel')}
        isBusy={deletePhotoMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => {
          if (!deletePhotoMutation.isPending) {
            setIsDeleteDialogOpen(false);
          }
        }}
      />

      {isChangePasswordOpen && (
        <ChangePasswordDialog
          onSubmit={handleChangePassword}
          onClose={() => {
            if (!updatePasswordMutation.isPending) {
              setIsChangePasswordOpen(false);
            }
          }}
          isSubmitting={updatePasswordMutation.isPending}
        />
      )}
    </div>
  );
}
